import { useEffect } from 'react';
import { getUsablePageHeight, REPORT_PAGE_METRICS } from './reportPageMetrics';

const HARD_BREAK_ATTR = 'data-report-hard-break';
const PAGE_ATTR = 'data-report-page';
const ORIGINAL_MARGIN_ATTR = 'data-report-original-margin-top';
const HEADING_KEEP_BUFFER_PX = 48;
const ATOMIC_KEEP_BUFFER_PX = 16;
const MIN_HEADING_REMAINING_SPACE_PX = 110;

const isPagedMode = (mode) => mode === 'strict' || mode === 'guides' || mode === 'paged_guides';

const toPixels = (value) => {
  const parsed = Number.parseFloat(String(value || '0'));
  return Number.isFinite(parsed) ? parsed : 0;
};

const getTopLevelBlocks = (contentRoot) => (
  Array.from(contentRoot?.children || []).filter((node) => node instanceof HTMLElement)
);

const resetHardPageStyles = (elements) => {
  elements.forEach((element) => {
    const originalMargin = element.getAttribute(ORIGINAL_MARGIN_ATTR);
    if (originalMargin != null) {
      element.style.marginTop = `${originalMargin}px`;
      element.removeAttribute(ORIGINAL_MARGIN_ATTR);
    }
    element.removeAttribute(HARD_BREAK_ATTR);
    element.removeAttribute(PAGE_ATTR);
  });
};

const getOuterBlockHeight = (element) => {
  const style = window.getComputedStyle(element);
  const marginTop = Math.max(0, toPixels(style.marginTop));
  const marginBottom = toPixels(style.marginBottom);
  return marginTop + element.offsetHeight + marginBottom;
};

const getRemainingHeight = (cursor, pageHeight) => {
  if (cursor <= 0) {
    return 0;
  }
  return Math.max(0, pageHeight - cursor);
};

const isHeadingBlock = (element) => {
  if (!element) {
    return false;
  }
  if (/^H[1-3]$/.test(element.tagName)) {
    return true;
  }
  return element.classList.contains('report-draft-editor__heading')
    || element.classList.contains('report-draft-editor__heading--h1')
    || element.classList.contains('report-draft-editor__heading--h2')
    || element.classList.contains('report-draft-editor__heading--h3');
};

const isCoverBlock = (element) => (
  Boolean(element?.classList.contains('report-draft-editor__cover-node-wrap'))
);

const isTocBlock = (element) => (
  Boolean(element?.classList.contains('report-draft-editor__toc'))
);

const isVisualBlock = (element) => (
  Boolean(element?.classList.contains('report-draft-editor__visual-node'))
);

const isTableBlock = (element) => (
  Boolean(element?.classList.contains('report-draft-editor__table'))
);

const isListBlock = (element) => (
  Boolean(element?.classList.contains('report-draft-editor__list'))
);

const isParagraphBlock = (element) => (
  Boolean(element?.classList.contains('report-draft-editor__paragraph'))
);

const isPlaceholderBlock = (element) => (
  Boolean(element?.classList.contains('report-draft-editor__placeholder'))
);

const isMetadataLikeBlock = (element) => (
  isTableBlock(element) || isListBlock(element)
);

const isTrivialBlock = (element) => {
  if (!element) {
    return true;
  }
  if (isPlaceholderBlock(element)) {
    return true;
  }
  if (isParagraphBlock(element)) {
    return element.textContent.trim().length === 0;
  }
  return false;
};

const isAtomicBlock = (element) => {
  if (!element) {
    return false;
  }
  if (isCoverBlock(element)) {
    return true;
  }
  if (isTocBlock(element)) {
    return true;
  }
  if (isVisualBlock(element)) {
    return true;
  }
  if (isTableBlock(element)) {
    return true;
  }
  if (isListBlock(element)) {
    return true;
  }
  return false;
};

const findNextMeaningfulIndex = (elements, startIndex) => {
  let index = startIndex;
  while (index < elements.length) {
    if (!isTrivialBlock(elements[index])) {
      return index;
    }
    index += 1;
  }
  return -1;
};

const buildPaginationGroups = (elements) => {
  const groups = [];
  let index = 0;
  while (index < elements.length) {
    const current = elements[index];
    if (!current) {
      index += 1;
      continue;
    }

    const currentIsHeading = isHeadingBlock(current);
    const currentIsAtomic = isAtomicBlock(current);
    let endIndex = index;

    if (currentIsHeading) {
      const nextMeaningfulIndex = findNextMeaningfulIndex(elements, index + 1);
      if (nextMeaningfulIndex > -1 && !isHeadingBlock(elements[nextMeaningfulIndex])) {
        endIndex = nextMeaningfulIndex;
        const firstCompanion = elements[nextMeaningfulIndex];
        const companionHeight = getOuterBlockHeight(firstCompanion);
        const maybeSecondCompanionIndex = findNextMeaningfulIndex(elements, nextMeaningfulIndex + 1);
        if (
          maybeSecondCompanionIndex > -1
          && !isHeadingBlock(elements[maybeSecondCompanionIndex])
          && isParagraphBlock(firstCompanion)
          && companionHeight < 210
          && (isMetadataLikeBlock(elements[maybeSecondCompanionIndex]) || isVisualBlock(elements[maybeSecondCompanionIndex]))
        ) {
          endIndex = maybeSecondCompanionIndex;
        }
      }
    }
    const forcePageBreakAfter = Array.from(
      { length: endIndex - index + 1 },
      (_, offset) => elements[index + offset]
    ).some((element) => isCoverBlock(element) || isTocBlock(element));

    groups.push({
      startIndex: index,
      endIndex,
      isHeadingGroup: currentIsHeading,
      isAtomic: currentIsAtomic,
      forcePageBreakAfter,
    });
    index = endIndex + 1;
  }
  return groups;
};

const getGroupHeight = (elements, startIndex, endIndex) => {
  let total = 0;
  for (let index = startIndex; index <= endIndex; index += 1) {
    total += getOuterBlockHeight(elements[index]);
  }
  return total;
};

const assignPageAttributesForGroup = ({
  elements,
  startIndex,
  endIndex,
  currentPage,
  cursor,
  contentPageHeight,
}) => {
  let nextCursor = cursor;
  let nextPage = currentPage;

  for (let index = startIndex; index <= endIndex; index += 1) {
    const element = elements[index];
    const blockHeight = getOuterBlockHeight(element);
    element.setAttribute(PAGE_ATTR, String(nextPage));
    const totalAfterPlacement = nextCursor + blockHeight;
    const pageSpans = Math.floor(totalAfterPlacement / contentPageHeight);
    nextCursor = totalAfterPlacement % contentPageHeight;
    nextPage += pageSpans;
  }

  return {
    cursor: nextCursor,
    currentPage: nextPage,
  };
};

const applyBreakBeforeGroupStart = ({
  groupStartElement,
  remainingHeight,
  metrics,
  contentTopInset,
  contentBottomInset,
}) => {
  const style = window.getComputedStyle(groupStartElement);
  const originalMarginTop = toPixels(style.marginTop);
  const spacer = Math.max(0, remainingHeight)
    + metrics.sheetGapPx
    + contentTopInset
    + contentBottomInset;
  groupStartElement.setAttribute(ORIGINAL_MARGIN_ATTR, String(originalMarginTop));
  groupStartElement.style.marginTop = `${originalMarginTop + spacer}px`;
  groupStartElement.setAttribute(HARD_BREAK_ATTR, 'true');
};

const applyHardPageFlow = (contentRoot, metrics) => {
  const elements = getTopLevelBlocks(contentRoot);
  if (elements.length === 0) {
    return;
  }

  resetHardPageStyles(elements);

  const pageHeight = getUsablePageHeight(metrics);
  const contentTopInset = Math.max(0, toPixels(metrics?.contentTopInsetPx));
  const contentBottomInset = Math.max(0, toPixels(metrics?.contentBottomInsetPx));
  const contentPageHeight = Math.max(
    1,
    pageHeight
      - contentTopInset
      - contentBottomInset
  );
  let cursor = 0;
  let currentPage = 1;
  const groups = buildPaginationGroups(elements);

  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    const group = groups[groupIndex];
    const groupStartElement = elements[group.startIndex];
    if (!groupStartElement) {
      continue;
    }

    const groupHeight = getGroupHeight(elements, group.startIndex, group.endIndex);
    if (groupHeight <= 0) {
      groupStartElement.setAttribute(PAGE_ATTR, String(currentPage));
      continue;
    }

    const remainingHeight = getRemainingHeight(cursor, contentPageHeight);
    const canGroupFitInPage = groupHeight <= contentPageHeight;
    const groupBuffer = group.isHeadingGroup
      ? HEADING_KEEP_BUFFER_PX
      : (group.isAtomic ? ATOMIC_KEEP_BUFFER_PX : 0);
    const bufferedGroupHeight = groupHeight + groupBuffer;
    const shouldBreakBeforeGroup = cursor > 0 && (
      (group.isHeadingGroup && remainingHeight < MIN_HEADING_REMAINING_SPACE_PX)
      || (canGroupFitInPage && bufferedGroupHeight > remainingHeight)
      || (!canGroupFitInPage && (group.isHeadingGroup || group.isAtomic))
    );

    if (shouldBreakBeforeGroup) {
      applyBreakBeforeGroupStart({
        groupStartElement,
        remainingHeight,
        metrics,
        contentTopInset,
        contentBottomInset,
      });
      cursor = 0;
      currentPage += 1;
    }

    const nextState = assignPageAttributesForGroup({
      elements,
      startIndex: group.startIndex,
      endIndex: group.endIndex,
      currentPage,
      cursor,
      contentPageHeight,
    });
    cursor = nextState.cursor;
    currentPage = nextState.currentPage;

    if (group.forcePageBreakAfter) {
      const shouldAdvancePage = cursor > 0;
      const nextGroup = groups[groupIndex + 1];
      if (nextGroup) {
        const nextGroupStartElement = elements[nextGroup.startIndex];
        if (nextGroupStartElement) {
          applyBreakBeforeGroupStart({
            groupStartElement: nextGroupStartElement,
            remainingHeight: getRemainingHeight(cursor, contentPageHeight),
            metrics,
            contentTopInset,
            contentBottomInset,
          });
        }
      }
      cursor = 0;
      if (shouldAdvancePage) {
        currentPage += 1;
      }
    }
  }
};

export function useHardPageFlow({
  contentRef,
  mode = 'strict',
  metrics = REPORT_PAGE_METRICS,
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) {
      const contentRoot = contentRef?.current;
      const blocks = getTopLevelBlocks(contentRoot);
      resetHardPageStyles(blocks);
      return undefined;
    }

    if (!isPagedMode(mode)) {
      const contentRoot = contentRef?.current;
      const blocks = getTopLevelBlocks(contentRoot);
      resetHardPageStyles(blocks);
      return undefined;
    }

    if (typeof window === 'undefined') {
      return undefined;
    }

    const contentRoot = contentRef?.current;
    if (!contentRoot) {
      return undefined;
    }

    let rafId = null;
    const schedule = () => {
      if (rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        applyHardPageFlow(contentRoot, metrics);
      });
    };

    schedule();

    const onViewportChange = () => schedule();
    window.addEventListener('resize', onViewportChange);

    const scrollParent = contentRoot.parentElement;
    if (scrollParent) {
      scrollParent.addEventListener('scroll', onViewportChange, { passive: true });
    }

    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => schedule());
      observer.observe(contentRoot);
    }

    return () => {
      const blocks = getTopLevelBlocks(contentRoot);
      resetHardPageStyles(blocks);
      window.removeEventListener('resize', onViewportChange);
      if (scrollParent) {
        scrollParent.removeEventListener('scroll', onViewportChange);
      }
      if (observer) {
        observer.disconnect();
      }
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [contentRef, metrics, mode, enabled]);
}

export default useHardPageFlow;
